/**
 * NFC login — writing a login card and redeeming one.
 *
 * A login card carries a single NDEF URL record pointing at this app with the
 * bearer token in the URL *fragment*: `https://<origin>/#nfc=<jwt>`. The token
 * is the same one the QR login mints (see `qrLoginApi`).
 *
 * The fragment is deliberate — it is never sent to the server, so the token
 * stays out of access logs and the reverse proxy, and `takeNfcTokenFromUrl`
 * scrubs it from the address bar the moment it is redeemed.
 *
 * A URL record is likewise deliberate: Android reads it in-app through this
 * API, and an iPhone (XS and newer) picks the same card up with OS background
 * tag reading and offers to open the URL — Safari has no Web NFC at all.
 * Writing needs `NDEFReader.write()`, so cards can only be provisioned from
 * Android.
 *
 * Requires a secure context (HTTPS or localhost), like the camera scanner.
 *
 * The Android app's WebView has no Web NFC; there the same three functions go
 * through the app's own NFC (`lib/nativeApp`), and the callers notice nothing.
 */

import {
    hasNfcInApp,
    scanNfcInApp,
    writeNfcUrlInApp
} from '../../lib/nativeApp';

/** The fragment key a login card's URL carries the token under. */
const TOKEN_HASH_KEY = 'nfc';

/** A JWT's shape — three base64url segments. Not a validation, just a filter. */
const JWT_PATTERN = /^[\w-]+\.[\w-]+\.[\w-]+$/;

/**
 * True when this browser can read and write NFC tags. Web NFC is Chromium on
 * Android only, plus the Android app — everything else falls back to the URL
 * the card carries.
 */
export function isNfcSupported(): boolean {
    return (
        hasNfcInApp() ||
        (typeof window !== 'undefined' && 'NDEFReader' in window)
    );
}

/** The fragment a login card's URL ends with, for `token`. */
export function nfcLoginHash(token: string): string {
    return `#${TOKEN_HASH_KEY}=${token}`;
}

/** The URL written onto a login card for `token`. */
export function nfcLoginUrl(token: string): string {
    return `${window.location.origin}/${nfcLoginHash(token)}`;
}

/** The login token a URL fragment carries, if it carries one. */
export function tokenFromHash(hash: string): string | null {
    const marker = `#${TOKEN_HASH_KEY}=`;
    if (!hash.startsWith(marker)) {
        return null;
    }
    const token = hash.slice(marker.length);
    return JWT_PATTERN.test(token) ? token : null;
}

/**
 * Pull a login token out of whatever a card holds — either the full login URL
 * or, if the card was written by some other tool, the bare token as text.
 */
export function tokenFromNfcPayload(payload: string): string | null {
    const start = payload.indexOf(`#${TOKEN_HASH_KEY}=`);
    if (start !== -1) {
        return tokenFromHash(payload.slice(start));
    }
    const token = payload.trim();
    return JWT_PATTERN.test(token) ? token : null;
}

/** The first login token among a card's texts, if any. */
function firstToken(texts: string[]): string | null {
    for (const text of texts) {
        const token = tokenFromNfcPayload(text);
        if (token) {
            return token;
        }
    }
    return null;
}

/** The first login token among an NDEF message's records, if any. */
function tokenFromNdefMessage(message: NDEFMessage): string | null {
    const texts: string[] = [];
    for (const record of message.records) {
        if (record.recordType !== 'url' && record.recordType !== 'text') {
            continue;
        }
        if (!record.data) {
            continue;
        }
        texts.push(
            new TextDecoder(record.encoding || 'utf-8').decode(record.data)
        );
    }
    return firstToken(texts);
}

/**
 * Redeem the token a login card's URL put in the address bar, on platforms that
 * cannot read the card in-app (an iPhone tapping a card opens this URL). The
 * fragment is stripped from the current history entry before returning, so the
 * token does not survive in a shared device's back stack.
 */
export function takeNfcTokenFromUrl(): string | null {
    const token = tokenFromHash(window.location.hash);
    if (!token) {
        return null;
    }
    window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
    );
    return token;
}

/**
 * Write a login card. Resolves once a card has been tapped and written, so the
 * caller shows a "hold the card against the device" prompt for the duration and
 * aborts via `signal` when the user gives up.
 */
export async function writeNfcLoginCard(
    token: string,
    signal: AbortSignal
): Promise<void> {
    if (hasNfcInApp()) {
        return writeNfcUrlInApp(nfcLoginUrl(token), signal);
    }
    const reader = new NDEFReader();
    await reader.write(
        { records: [{ recordType: 'url', data: nfcLoginUrl(token) }] },
        { signal }
    );
}

/**
 * Watch for login cards until `signal` aborts. `onCard` fires per tap with the
 * token, or with null when the card holds no login — the reader cannot tell the
 * two apart before reading, so "wrong card" is a normal outcome, not an error.
 * Rejects if scanning can't start (permission denied, NFC switched off).
 */
export async function scanNfcLoginCard(
    onCard: (token: string | null) => void,
    signal: AbortSignal
): Promise<void> {
    if (hasNfcInApp()) {
        return scanNfcInApp((texts) => onCard(firstToken(texts)), signal);
    }
    const reader = new NDEFReader();
    reader.onreading = (event) => onCard(tokenFromNdefMessage(event.message));
    await reader.scan({ signal });
}
